import { Controller } from "@hotwired/stimulus";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

/**
 * Tiptap WYSIWYG Editor Controller
 *
 * Usage:
 * <div data-controller="tiptap" data-tiptap-content-value="<p>Initial content</p>">
 *   <div data-tiptap-target="toolbar">
 *     <button type="button" data-action="click->tiptap#toggleBold">B</button>
 *     <button type="button" data-action="click->tiptap#toggleItalic">I</button>
 *     ...
 *   </div>
 *   <div data-tiptap-target="editor"></div>
 *   <input type="hidden" data-tiptap-target="input" name="organization[booking_policy]">
 * </div>
 */
export default class extends Controller {
  static targets = ["editor", "input", "toolbar"];
  static values = {
    content: { type: String, default: "" },
  };

  connect() {
    this.initializeEditor();
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy();
    }
  }

  initializeEditor() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3, 4],
          },
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
      ],
      content: this.contentValue || "",
      editorProps: {
        attributes: {
          class:
            "prose prose-sm max-w-none focus:outline-none min-h-[180px] p-4",
        },
        // Handle HTML paste - parse and convert to editor format
        transformPastedHTML(html) {
          return html;
        },
      },
      // Enable parsing of pasted HTML content
      parseOptions: {
        preserveWhitespace: "full",
      },
      onUpdate: ({ editor }) => {
        // Sync HTML content to hidden input
        this.inputTarget.value = editor.getHTML();
      },
      onCreate: ({ editor }) => {
        // Initial sync
        this.inputTarget.value = editor.getHTML();
      },
    });
  }

  // ===== Formatting Actions =====

  toggleBold(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleBold().run();
    this.updateToolbarState();
  }

  toggleItalic(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleItalic().run();
    this.updateToolbarState();
  }

  toggleStrike(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleStrike().run();
    this.updateToolbarState();
  }

  // ===== Heading Actions =====

  toggleHeading(event) {
    event.preventDefault();
    const level = parseInt(event.currentTarget.dataset.level || "2", 10);
    this.editor.chain().focus().toggleHeading({ level }).run();
    this.updateToolbarState();
  }

  // ===== List Actions =====

  toggleBulletList(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleBulletList().run();
    this.updateToolbarState();
  }

  toggleOrderedList(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleOrderedList().run();
    this.updateToolbarState();
  }

  // ===== Block Actions =====

  toggleBlockquote(event) {
    event.preventDefault();
    this.editor.chain().focus().toggleBlockquote().run();
    this.updateToolbarState();
  }

  addHorizontalRule(event) {
    event.preventDefault();
    this.editor.chain().focus().setHorizontalRule().run();
  }

  // ===== Link Actions =====

  addLink(event) {
    event.preventDefault();

    // Check if there's already a link
    const previousUrl = this.editor.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl || "https://");

    if (url === null) {
      // User cancelled
      return;
    }

    if (url === "") {
      // Remove link
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Set link
    this.editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }

  removeLink(event) {
    event.preventDefault();
    this.editor.chain().focus().unsetLink().run();
  }

  // ===== Utility Methods =====

  updateToolbarState() {
    // Update active states for toolbar buttons
    if (!this.hasToolbarTarget) return;

    const buttons = this.toolbarTarget.querySelectorAll("[data-format]");
    buttons.forEach((button) => {
      const format = button.dataset.format;
      const isActive = this.isActive(format);

      if (isActive) {
        button.classList.add("bg-gray-200", "border-gray-400");
        button.classList.remove("bg-white");
      } else {
        button.classList.remove("bg-gray-200", "border-gray-400");
        button.classList.add("bg-white");
      }
    });
  }

  isActive(format) {
    switch (format) {
      case "bold":
        return this.editor.isActive("bold");
      case "italic":
        return this.editor.isActive("italic");
      case "strike":
        return this.editor.isActive("strike");
      case "h2":
        return this.editor.isActive("heading", { level: 2 });
      case "h3":
        return this.editor.isActive("heading", { level: 3 });
      case "bulletList":
        return this.editor.isActive("bulletList");
      case "orderedList":
        return this.editor.isActive("orderedList");
      case "blockquote":
        return this.editor.isActive("blockquote");
      case "link":
        return this.editor.isActive("link");
      default:
        return false;
    }
  }

  // Focus the editor
  focus() {
    this.editor.commands.focus();
  }
}
